import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef } from "react";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorldMapEvents } from "@utils/track-event/track-event-list";

/** Outcome of the projects fetch reported with the load event. */

/** Result of the projects fetch, reported once the request settles. */

/** Timing and count tracking for a single projects map page load. */

/**
 * Tracks how long the projects map page takes to become usable and how many
 * projects it loaded, emitting a single `projectsMapLoaded` Amplitude event once
 * both the map view is ready and the projects request has settled.
 *
 * The map view and the projects request load in parallel, so the durations are
 * measured independently from a shared mount timestamp and the total is the later
 * of the two. All durations use the monotonic `performance.now()` clock.
 */
export function useProjectsMapLoadTracking() {
  const $ = _c(11);
  const {
    trackEvent
  } = useTrackEvent();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = performance.now();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const mountAtRef = useRef(t0);
  const mapReadyAtRef = useRef(null);
  const fetchStartAtRef = useRef(null);
  const fetchResultRef = useRef(null);
  const hasFiredRef = useRef(false);
  let t1;
  if ($[1] !== trackEvent) {
    t1 = () => {
      const mapReadyAt = mapReadyAtRef.current;
      const fetchResult = fetchResultRef.current;
      if (hasFiredRef.current || mapReadyAt === null || fetchResult === null) {
        return;
      }
      hasFiredRef.current = true;
      const mountAt = mountAtRef.current;
      const fetchStartAt = fetchStartAtRef.current ?? mountAt;
      trackEvent({
        name: WorldMapEvents.projectsMapLoaded,
        props: {
          outcome: fetchResult.outcome,
          projectCount: fetchResult.projectCount,
          mappableProjectCount: fetchResult.mappableProjectCount,
          projectsLoadDurationMs: Math.round(fetchResult.settledAt - fetchStartAt),
          mapReadyDurationMs: Math.round(mapReadyAt - mountAt),
          totalReadyDurationMs: Math.round(Math.max(mapReadyAt, fetchResult.settledAt) - mountAt)
        }
      });
    };
    $[1] = trackEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const fireEventIfReady = t1;
  let t2;
  if ($[3] !== fireEventIfReady) {
    t2 = () => {
      if (mapReadyAtRef.current === null) {
        mapReadyAtRef.current = performance.now();
      }
      fireEventIfReady();
    };
    $[3] = fireEventIfReady;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleMapReady = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (fetchStartAtRef.current === null) {
        fetchStartAtRef.current = performance.now();
      }
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleProjectsFetchStart = t3;
  let t4;
  if ($[6] !== fireEventIfReady) {
    t4 = result => {
      if (fetchResultRef.current === null) {
        fetchResultRef.current = {
          ...result,
          settledAt: performance.now()
        };
      }
      fireEventIfReady();
    };
    $[6] = fireEventIfReady;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleProjectsFetchEnd = t4;
  let t5;
  if ($[8] !== handleMapReady || $[9] !== handleProjectsFetchEnd) {
    t5 = {
      handleMapReady,
      handleProjectsFetchStart,
      handleProjectsFetchEnd
    };
    $[8] = handleMapReady;
    $[9] = handleProjectsFetchEnd;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}